import { intersection, difference } from 'lodash';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { DEFAULT_FACTORS } from '../../../../constants/Constants';
import { DaySteps } from '../../../../entities/Day';
import { stepsToDays } from '../../../../entities/Steps';
import { sortNumbers } from '../../../../utils/Array';
import Checkbox from '../../../Inputs/Checkbox/Checkbox';
import Index from '../../../Ui/CheckboxWrap';
import { Radio } from '../../../Inputs/Radio';
import { Row, Col } from 'react-bootstrap';

const minDiff = (values: Array<number>): number => {
  let sorted = sortNumbers(values);

  return sorted.reduce((mem, value, index) => {
    const nextValue = sorted[index + 1];

    if (!nextValue) {
      return mem;
    }

    const diff = sorted[index + 1] - value;

    if (diff < mem || mem === 0) {
      mem = diff;
    }

    return mem;
  }, 0);
};

const addDayStepsSelected = (values: Array<number>, dayOption: DaySteps, increment: number) => {
  const stepsWithIncrement = dayOption.steps.filter(step => step % increment === 0);
  const inter = intersection(values, stepsWithIncrement);

  return inter.length > 0 && inter.length === stepsWithIncrement.length;
}

const StepsByDayAndIncrementControl = (props: {
  values: Array<number>;
  valuesPool: Array<number>;
  onChange: (values: Array<number>, strategy: 'add' | 'remove' | 'overwrite') => void;
}) => {
  const { values, valuesPool, onChange } = props;
  const dayOptions = useMemo(() => stepsToDays(valuesPool), [valuesPool]);
  const minIncrement = useMemo(() => minDiff(valuesPool), [valuesPool]);
  const increments = DEFAULT_FACTORS.filter(factor => factor >= minIncrement);
  const nameSuffix = valuesPool[0];
  const minSelectedIncrement = useMemo(() => minDiff(values), [values]);

  const [selectedIncrement, setSelectedIncrement] = useState<number>(minSelectedIncrement || minIncrement);
  const [selectedDays, setSelectedDays] = useState<Array<number>>([]);

  useEffect(() => {
    const days = dayOptions.reduce((acc: Array<number>, dayOption) => {
      if (addDayStepsSelected(values, dayOption, selectedIncrement)) {
        acc.push(dayOption.day);
      }


      return acc;
    }, []);

    setSelectedDays(days);
  }, [dayOptions, values, selectedIncrement])

  const onStateChange = useCallback((newSelectedDays, newSelectedIncrement) => {
    let strategy: 'add' | 'remove' | 'overwrite';
    let options: Array<DaySteps>;

    if (selectedIncrement !== newSelectedIncrement) {
      if (newSelectedDays.length === 0) {
        return;
      }

      strategy = 'overwrite';
      options = dayOptions.filter(option => selectedDays.includes(option.day));
    } else if (newSelectedDays.length > selectedDays.length) {
      strategy = 'add';
      options = dayOptions.filter(option => newSelectedDays.includes(option.day));
    } else {
      strategy = 'remove';
      options = dayOptions.filter(option => difference(selectedDays, newSelectedDays).includes(option.day));
    }

    const stepsToChange = options.reduce((mem: Array<number>, option) => {
      mem = mem.concat(
        strategy === 'remove' ?
          [...option.steps]
          : option.steps.filter(step => step % newSelectedIncrement === 0)
      );

      return mem;
    }, []);

    onChange(stepsToChange, strategy);
  }, [dayOptions, onChange, selectedDays, selectedIncrement]);

  const onDayCheckboxChange = (dayOption: DaySteps, checked: boolean) => {
    const selected = checked ? selectedDays.concat(dayOption.day) : selectedDays.filter(d => d !== dayOption.day);
    onStateChange(selected, selectedIncrement);
  };

  const onIncrementChange = (increment: string | Array<string>) => {
    onStateChange(selectedDays, parseInt(increment as string, 10));
    setSelectedIncrement(parseInt(increment as string, 10));
  };

  return (
    <div className="steps-by-day">
      <Row>
        <Col xs={8} sm={6}>
          <Index>
            {
              dayOptions.map(dayOption => {
                const stepsWithIncrement = dayOption.steps.filter(step => step % selectedIncrement === 0);
                const inter = intersection(values, stepsWithIncrement);
                const isIndeterminate = inter.length > 0 && inter.length < stepsWithIncrement.length;

                const checked = selectedDays.includes(dayOption.day) && !isIndeterminate;

                return <Checkbox
                  key={dayOption.day}
                  label={`Day ${dayOption.day}`}
                  value={dayOption.day}
                  checked={checked}
                  indeterminate={isIndeterminate}
                  name={`step-ranges-${nameSuffix}`}
                  onChange={(checked) => onDayCheckboxChange(dayOption, checked)}
                />;
              })
            }
          </Index>
        </Col>

        <Col xs={4} sm={6}>
          {
            increments.map(increment => (
              <Radio
                value={increment.toString()}
                key={increment}
                label={`${increment} hourly`}
                name={`step-increments-${nameSuffix}`}
                onChange={onIncrementChange}
                checked={increment === selectedIncrement}
              />)
            )
          }
        </Col>

      </Row>
    </div>
  );
};

export default StepsByDayAndIncrementControl;
